import {
  type OasDocument3XExtended,
  type OasWebPageTreeNode,
  isNonNullable,
  isOasWebMenuNode,
  isOasWebPageApiTable,
  isOasWebTabsPage,
} from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import type { RoutesWithApiEndpoint } from 'containers/App/routing/RoutesWithApiEndpoint';
import { array, option } from 'fp-ts';
import { flow, pipe } from 'fp-ts/lib/function';
import { getXTableOas } from 'utils/oas/getXTableOas';

interface FoundPaths {
  [url: string]: string;
}

const getNamesFromNodes = (
  nodes: OasWebPageTreeNode[],
  path: string[],
  routesWithApiEndpoint: RoutesWithApiEndpoint,
  oas: OasDocument3XExtended,
): FoundPaths => {
  return nodes.reduce<FoundPaths>((acc, node) => {
    if (isOasWebMenuNode(node)) {
      Object.assign(
        acc,
        getNamesFromNodes(
          node.submenu,
          [...path, node.title],
          routesWithApiEndpoint,
          oas,
        ),
      );
      return acc;
    }
    if (isOasWebTabsPage(node)) {
      Object.assign(
        acc,
        getNamesFromNodes(
          node.tabs,
          [...path, node.title],
          routesWithApiEndpoint,
          oas,
        ),
      );
      return acc;
    }
    if (isOasWebPageApiTable(node)) {
      return pipe(
        getXTableOas(oas, node.apiTable),
        option.fromNullable,
        option.chainNullableK(prop('webPath')),
        option.map((webPath) => {
          acc[webPath] = `(${path.join(' - ')})`;
          return acc;
        }),
        option.getOrElse(() => acc),
      );
    }
    return acc;
  }, {});
};

export const getApiTablePaths = (
  oas: OasDocument3XExtended | null,
  routesWithApiEndpoint: RoutesWithApiEndpoint,
): FoundPaths => {
  const xWebPages = oas?.['x-webPages'];
  return pipe(
    xWebPages,
    option.fromPredicate(isNonNullable),
    option.map(
      flow(
        Object.entries,
        array.reduce(
          {},
          (
            acc,
            [key, nodes]: [key: string, nodes: OasWebPageTreeNode[]],
          ): FoundPaths => {
            Object.assign(
              acc,
              getNamesFromNodes(
                nodes,
                [`${key.charAt(0).toUpperCase()}${key.slice(1)}`],
                routesWithApiEndpoint,
                oas!,
              ),
            );
            return acc;
          },
        ),
      ),
    ),
    option.getOrElse(() => ({})),
  );
};
